import { useAllStatusRideList, useRideList } from "api/ride";
import CustomReactSelect from "components/CustomReactSelect";
import PropTypes from "prop-types";
import { useCallback, useMemo } from "react";


const RideSelector = ({
    value,
    onChange,
    label = "Ride",
    placeholder = "Select...",
    isMulti = false,
    required = false,
    disabled = false,
    isFilter = false,
    showSelectAllButton = false,
    ...props
}) => {
    const rideQuery = useRideList();
    const allRideQuery = useAllStatusRideList();
    const allRides = useMemo(
        () => (allRideQuery.data ?? []).map(ride => ({ label: ride.status?.id !== undefined && ride.status.id !== 10 ? `${ride.name} (${ride.status?.name})` : ride.name, value: ride.id })),
        [allRideQuery.data]
    );

    const rides = useMemo(
        () => (rideQuery.data ?? []).map(ride => ({ label: ride.name, value: ride.id })),
        [rideQuery.data]
    );

    const loadOptions = useCallback(async (inputValue = '') => {
        try {
            let dataOptions = rides;

            if (isFilter && !dataOptions.some(option => option.value === "")) {
                dataOptions = [{ label: "All", value: "" }, ...dataOptions];
            }

            if (inputValue) {
                return dataOptions.filter(option => option.label.toLowerCase().includes(inputValue.toLowerCase()));
            }

            return dataOptions;
        } catch (error) {
            return [];
        }
    }, [rides, isFilter]);

    const availableOptions = useMemo(() => {
        let options = [...rides];
        if (isFilter && !options.some(option => option.value === "")) {
            options = [{ label: "All", value: "" }, ...options];
        }
        return options;
    }, [rides, isFilter]);

    const allOptionValues = useMemo(
        () => availableOptions.filter(option => option.value !== "").map(option => option.value),
        [availableOptions]
    );

    const selectedValues = useMemo(
        () => (Array.isArray(value) ? value.filter(v => v !== "") : []),
        [value]
    );

    const isAllSelected = useMemo(
        () => allOptionValues.length > 0 && selectedValues.length === allOptionValues.length,
        [allOptionValues.length, selectedValues.length]
    );

    const getSelectedValue = () => {
        if (!value && value !== "") return null;

        // Build complete options list including filter option
        if (isMulti) {
            const lookup = [...availableOptions, ...allRides.filter(r => !availableOptions.some(o => o.value === r.value))];
            const selected = lookup.filter(option => value.includes(option.value));
            return selected.length > 0 ? selected : null;
        }

        const selected = availableOptions.find(option => option.value === value) || allRides.find(option => option.value === value);
        return selected || null;
    };

    const handleChange = (selectedOption) => {
        if (isMulti) {
            const selectedValues = selectedOption ? selectedOption.map(opt => opt.value) : [];
            if (onChange) {
                onChange(selectedValues, selectedOption);
            }
        } else {
            const selectedValue = selectedOption ? selectedOption.value : (isFilter ? "" : null);
            if (onChange) {
                onChange(selectedValue, selectedOption);
            }
        }
    };

    const handleSelectAllToggle = () => {
        if (disabled || !isMulti) return;

        const nextValues = isAllSelected ? [] : allOptionValues;
        const nextSelectedOptions = availableOptions.filter(option => nextValues.includes(option.value));

        if (onChange) {
            onChange(nextValues, nextSelectedOptions);
        }
    };


    return (
        <>
            {isMulti && showSelectAllButton && (
                <div style={{ marginBottom: 8, display: "flex", justifyContent: "flex-end" }}>
                    <button
                        type="button"
                        onClick={handleSelectAllToggle}
                        disabled={disabled || allOptionValues.length === 0}
                        style={{
                            border: "1px solid #bdbdbd",
                            borderRadius: 6,
                            background: "#fff",
                            padding: "4px 10px",
                            cursor: disabled || allOptionValues.length === 0 ? "not-allowed" : "pointer",
                            fontSize: 12,
                        }}
                    >
                        {isAllSelected ? "Clear All" : "Select All"}
                    </button>
                </div>
            )}

            <CustomReactSelect
                async
                label={label}
                isMulti={isMulti}
                isSearchable
                value={getSelectedValue()}
                loadOptions={loadOptions}
                defaultOptions={rides}
                onChange={handleChange}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                noOptionsMessage={() => "No rides found"}
                {...props}
            />
        </>
    );
};

RideSelector.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
        PropTypes.string,
        PropTypes.number,
    ]),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    isMulti: PropTypes.bool,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    showSelectAllButton: PropTypes.bool,
};

export default RideSelector;
